import {
  isDirectProviderConnection,
  isFoodHubProviderConnection,
  isKitchenHubProviderConnection,
  renderProviderConnectionTypeName,
  renderProviderName,
} from '../../helpers/helpers';
import { ProviderConnectionType } from '../../types/api/location.types';
import {
  LocationPayoutService,
  PayoutConfigResponse,
  PayoutModelType,
  PayoutProviderRule,
  PayoutTier,
} from '../../types/location.types';
import { Provider, ProviderType } from '../../types/shared.types';

/** Which of the two values an exception may carry on a given payout model. */
export type ProviderRuleAllowance = {
  percentage: boolean;
  flatFee: boolean;
};

/**
 * An exception always follows the model of the level it sits on - the API
 * rejects a flat fee on a bracket config, and a percentage on a flat fee one.
 */
export const providerRuleAllowance = (
  model: PayoutModelType
): ProviderRuleAllowance => ({
  percentage: model !== PayoutModelType.flat_fee,
  flatFee: model !== PayoutModelType.bracket,
});

/** A tier only exists on the hybrid model, where both values are allowed. */
export const TIER_RULE_ALLOWANCE: ProviderRuleAllowance = {
  percentage: true,
  flatFee: true,
};

/**
 * Exceptions only make sense for `vGrubs` (VRs), the one service whose orders
 * arrive from more than one provider. The API answers 400 for the others.
 */
export const serviceSupportsProviderRules = (service: LocationPayoutService) =>
  service === LocationPayoutService.vGrubs;

/**
 * The providers whose orders can come in for the vGrubs service, taken from
 * the app's own `Provider` enum so the exception picker offers exactly what
 * Manage Providers does. The backend's `ProviderType` is wider (marketplaces
 * no location here is connected to), and a rule still accepts any of it -
 * this is only what the UI offers.
 *
 * Left out: the providers of the other payout services, and `vGrubs` itself,
 * which the API does not accept as an order provider.
 *
 * POS variants stay separate entries because they are separate providers - a
 * rule that should cover both has to list both.
 */
const NON_VGRUBS_PROVIDERS: Provider[] = [
  Provider.vGrubs,
  Provider.bytes,
  Provider.kanekt365,
  Provider.vGrubsAI,
];

export const PAYOUT_RULE_PROVIDERS = Object.values(Provider).filter(
  (provider) => !NON_VGRUBS_PROVIDERS.includes(provider)
) as unknown as ProviderType[];

/**
 * The order sources an exception can be narrowed to, in the order offered.
 * These are the ways a provider can be connected: the same Uber Eats order is
 * paid differently depending on which one it came in over.
 */
export const PAYOUT_RULE_SOURCES: ProviderConnectionType[] = [
  ProviderConnectionType.thirdParty,
  ProviderConnectionType.direct,
  ProviderConnectionType.foodhub,
];

/**
 * The sources a provider's orders can actually arrive over, read off the same
 * helpers Manage Providers connects with: a marketplace whose connect modal
 * offers "Choose Ordering Service" can come in either way, a direct connection
 * only as itself, everything else through KitchenHub. Keeps the picker from
 * offering a source that would silently never match an order.
 */
export const sourcesForProvider = (
  provider: ProviderType
): ProviderConnectionType[] => {
  // The helpers are typed against the narrower `Provider`; the two enums share
  // their strings, the same cast the provider picker makes.
  const value = provider as unknown as Provider;

  // Local For You is connected over FoodHub without being asked - the modal
  // hides the choice for it and sends `shouldUseFoodhub: true` regardless.
  if (value === Provider.localForYou) return [ProviderConnectionType.foodhub];

  if (isFoodHubProviderConnection(value))
    return [ProviderConnectionType.foodhub, ProviderConnectionType.thirdParty];

  // Asked before KitchenHub, which `bytes` and `vGrubsAI` also satisfy: the
  // connect modal takes the direct branch for them and never redirects.
  if (isDirectProviderConnection(value)) return [ProviderConnectionType.direct];

  if (isKitchenHubProviderConnection(value))
    return [ProviderConnectionType.thirdParty];

  return [];
};

/**
 * The sources worth offering for the providers an exception covers. No
 * providers picked yet means nothing narrows the list.
 */
export const sourcesForProviders = (
  providers: ProviderType[]
): ProviderConnectionType[] =>
  providers.length
    ? PAYOUT_RULE_SOURCES.filter((source) =>
        providers.some((provider) =>
          sourcesForProvider(provider).includes(source)
        )
      )
    : PAYOUT_RULE_SOURCES;

/** Display name for one provider, used in the validation messages. */
export const payoutProviderLabel = (provider: ProviderType | string): string =>
  renderProviderName(provider, provider) ?? provider;

/**
 * Display name for one order source. Deliberately the same names Manage
 * Providers and the order details pill use (`thirdParty` reads "KitchenHub"),
 * so an exception can be matched to the source shown on an actual order.
 */
export const payoutSourceLabel = (
  source: ProviderConnectionType | string
): string => renderProviderConnectionTypeName(source) ?? source;

export const createEmptyProviderRule = (): PayoutProviderRule => ({
  providers: [],
  sources: [],
  percentage: null,
  flatFee: null,
});

/**
 * Whether two exceptions on the same level fight over a provider they share.
 * One that names no source is the catch-all for its providers and collides
 * only with another catch-all; two that name sources collide when they share
 * one. That is what lets "Uber Eats everywhere, 60%" sit next to "Uber Eats
 * via FoodHub, 70%", which is the whole point of the field.
 */
const sourcesCollide = (
  a: ProviderConnectionType[],
  b: ProviderConnectionType[]
): boolean =>
  (!a.length && !b.length) || a.some((source) => b.includes(source));

/**
 * Providers already claimed elsewhere on the same level, for the sources this
 * exception covers. The API rejects the same provider and source in two rules
 * of one level, so the select greys those out instead of letting the admin
 * build a payload that can't be saved. Changing the sources changes the list.
 */
export const providersClaimedByOtherRules = (
  rules: PayoutProviderRule[],
  index: number
): ProviderType[] =>
  rules.flatMap((rule, i) =>
    i !== index && sourcesCollide(rule.sources, rules[index].sources)
      ? rule.providers
      : []
  );

/**
 * True when every base amount lands in some tier, which makes the config level
 * of a hybrid unreachable - both its own values and its exceptions.
 */
export const tiersCoverAllAmounts = (tiers: PayoutTier[]): boolean => {
  if (!tiers.length) return false;

  const sorted = [...tiers].sort((a, b) => a.minAmount - b.minAmount);
  if (sorted[0].minAmount > 0) return false;

  for (let i = 0; i < sorted.length - 1; i++) {
    // An open ended tier swallows everything above it, so nothing after it can
    // leave a gap.
    if (sorted[i].maxAmount == null) return true;
    if ((sorted[i].maxAmount as number) < sorted[i + 1].minAmount) return false;
  }

  return sorted[sorted.length - 1].maxAmount == null;
};

/**
 * Drops the value the level's model doesn't use. The editor hides that input,
 * but a value typed before a model switch stays in state, and sending it is a
 * 400 - same reason `toPayoutConfigDto` rebuilds the config from scratch.
 */
export const sanitizeProviderRules = (
  rules: PayoutProviderRule[],
  allow: ProviderRuleAllowance
): PayoutProviderRule[] =>
  rules.map((rule) => ({
    providers: [...rule.providers],
    // Always sent, never omitted: the API reads a missing list as "every
    // source", which would widen an exception the admin never touched.
    sources: [...(rule.sources ?? [])],
    percentage: allow.percentage ? rule.percentage : null,
    flatFee: allow.flatFee ? rule.flatFee : null,
  }));

/**
 * Mirrors the API's rules for one level's exceptions, judged on the sanitized
 * values so the message matches what would actually be sent. `at` names the
 * level, e.g. "Bracket Percentage (VRs) - rule 2".
 */
export const validateProviderRules = (
  rules: PayoutProviderRule[],
  allow: ProviderRuleAllowance,
  at: string
): string | null => {
  // Keyed on provider and source, since an exception is only a duplicate of
  // another when they would both claim the same order. `null` stands for the
  // catch-all an exception with no sources is.
  const seen = new Map<string, number>();

  for (let i = 0; i < rules.length; i++) {
    const rule = rules[i];
    const where = `${at} - provider exception ${i + 1}`;

    if (!rule.providers.length) return `${where}: pick at least one provider.`;

    const sources: Array<ProviderConnectionType | null> = rule.sources.length
      ? rule.sources
      : [null];

    for (const provider of rule.providers) {
      for (const source of sources) {
        const key = `${provider}|${source ?? ''}`;
        const previous = seen.get(key);

        if (previous != null)
          return `${where}: ${payoutProviderLabel(provider)}${
            source ? ` via ${payoutSourceLabel(source)}` : ''
          } is already covered by provider exception ${previous + 1}.`;

        seen.set(key, i);
      }
    }

    const percentage = allow.percentage ? rule.percentage : null;
    const flatFee = allow.flatFee ? rule.flatFee : null;

    if (percentage == null && flatFee == null)
      return `${where}: set ${
        allow.percentage && allow.flatFee
          ? 'a percentage, a flat fee, or both'
          : allow.percentage
          ? 'a percentage'
          : 'a flat fee'
      }.`;

    if (percentage != null && !Number.isInteger(percentage))
      return `${where}: percentage must be a whole number.`;

    if (percentage != null && (percentage < 0 || percentage > 100))
      return `${where}: percentage must be between 0 and 100.`;

    if (flatFee != null && flatFee < 0)
      return `${where}: flat fee can't be negative.`;
  }

  return null;
};

/** How many exceptions a config carries in total, tiers included. */
export const countProviderRules = (config: PayoutConfigResponse): number =>
  (config.providerRules?.length ?? 0) +
  (config.tiers ?? []).reduce(
    (total, tier) => total + (tier.providerRules?.length ?? 0),
    0
  );
